import { defineComponent as _defineComponent } from 'vue'
import useProfileState from '@/state/profile/profileState';
import useProfilePreview from '@/state/profile/profilePreview';
import BCXAvatar from '@/components/molecules/BCXAvatar.vue';
import {
  computed,
  nextTick,
  provide,
  ref,
  watchEffect,
  watch
} from 'vue';
import ProfileEditActionButtons from '@/views/profile/components/ProfileEditActionButtons.vue';
import ProfileSectionBusyOverlay from '@/views/profile/components/ProfileSectionBusyOverlay.vue';
import BCXSearchSelect from '@/components/molecules/BCXSearchSelect.vue';
import IconAvatarEdit from '@/components/svg/avatar-circle.svg?inline';
import { updateAvatar, updateBasicData } from '@/services/Profile/Api';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import { getItemByValue, getValueByText } from '@/utils/selectItemTools';
import { ProfileMaxChars } from '@/models/Profile';
import useIsLeavingDirty from '@/utils/isLeavingDirty';
import useUser from '@/mixins/useUser';
import { useRoute } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import { asyncComputed } from '@vueuse/core';
import { extractCityName } from '@/utils/user';
import { SelectItem } from '@/models/SelectItem';
import { City } from '@/models/Geo';
import useAddressTools from '@/mixins/useAddressTools';
import DataNotice from '@/views/profile/components/DataNotice.vue';
import normalizeAvatarImage from '@/utils/normalizeAvatarImage';
import AvatarUpload from '../AvatarUpload.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileBasicEditor',
  props: {
    showDataNotice: { type: Boolean, default: true }
  },
  setup(__props: any) {



const { user, profile } = useProfileState();
const { avatarPreview, setAvatarPreview } = useProfilePreview();
const route = useRoute();

provide('currentEditSection', ref('basic'));
const isBusy = ref(false);
provide('profileSectionBusy', isBusy);

const { tUserFallback: t } = useUser(user);
const { isFreelancer } = useUser('self');
const { isDirty } = useIsLeavingDirty();
const { countries } = useAddressTools();
const store = useStore();

const country = ref('');
const city = ref('');
const summary = ref('');
const avatarDialog = ref(false);
const avatarRerenderKey = ref(0);
const loadingAnimation = 1;
const loadingAnimationSize = 50;
const avatarPreviewSrc = computed(() => (
  avatarPreview.value ? `data:${avatarPreview.value.imageType};base64,${avatarPreview.value.imageData}` : ''
));
const profileInitialValue = ref({
  city: '',
  countryIsoCode: '',
  profileSummary: ''
});
const hydrationRunId = ref(0);
const isHydratingForm = ref(false);
const countryTouched = ref(false);
const cityTouched = ref(false);
const summaryTouched = ref(false);

const selectedCity = ref({
  name: '',
  postalCode: '',
  countryCode: '',
});

const placeText = ({ placeName, postalCode }: any) => (postalCode || placeName ? `${postalCode}, ${placeName}` : '');

const fetchCities = (countryId: string, searchString: string) => {
  if (countryId) {
    store.dispatch('projectForum/fetchCities', { countryId, searchString });
  }
};

const inputCity = (value: string) => {
  fetchCities(country.value, extractCityName(value, 'zip'));
};

const setCityValues = ({ placeName, postalCode, countryCode }: Record<string, string>) => {
  selectedCity.value.name = placeName ?? '';
  selectedCity.value.postalCode = postalCode ?? '';
  selectedCity.value.countryCode = countryCode ?? '';
};

const cities = asyncComputed<SelectItem[]>(
  async () => {
    const { cities: citiesList }: { cities: City[] } = store.state.projectForum;
    return citiesList.map(({
      placeName,
      postalCode
    }) => {
      const formattedPlaceName = `${postalCode}, ${placeName.split(',')[0]?.trim() ?? placeName}`;
      return {
        value: formattedPlaceName,
        text: formattedPlaceName
      };
    });
  },
  []
);

const selectCity = (value: string) => {
  cities.value.forEach((city: any) => {
    if (placeText(city) === value) {
      setCityValues({
        placeName: city.placeName,
        postalCode: city.postalCode,
        countryCode: city.countryCode
      });
    }
  });
};

function getCountryFromUser(profileBasic = profile.value?.basic) {
  const country = profileBasic?.country ?? '';
  if (!country) return '';
  if (getItemByValue(countries.value, country)) return country.toString();
  return getValueByText(countries.value, country).toString();
}

const applyProfileBasicToForm = () => {
  const profileBasic = profile.value?.basic;
  if (!profileBasic || !countries.value?.length) return;

  const countryIsoCode = getCountryFromUser(profileBasic);
  const currentHydrationRunId = ++hydrationRunId.value;

  isHydratingForm.value = true;
  if (!countryTouched.value) {
    country.value = countryIsoCode;
  }
  if (!countryTouched.value && !cityTouched.value) {
    city.value = profileBasic.city ?? '';
    setCityValues({
      countryCode: countryIsoCode,
      placeName: extractCityName(profileBasic.city ?? ''),
      postalCode: extractCityName(profileBasic.city ?? '', 'zip')
    });
  }
  if (!summaryTouched.value) {
    summary.value = profileBasic.profileSummary ?? '';
  }
  profileInitialValue.value = {
    city: profileBasic.city ?? '',
    countryIsoCode,
    profileSummary: profileBasic.profileSummary ?? ''
  };
  nextTick(() => {
    if (hydrationRunId.value === currentHydrationRunId) {
      isHydratingForm.value = false;
    }
  });
};

watchEffect(() => {
  if (route.name === 'profile') {
    if (city.value !== profileInitialValue.value.city
      || country.value !== profileInitialValue.value.countryIsoCode
      || summary.value !== profileInitialValue.value.profileSummary) {
      isDirty.value = true;
    } else {
      isDirty.value = false;
    }
  }
});

watch(city, (newValue, oldValue) => {
  if (isHydratingForm.value || newValue === oldValue) return;
  cityTouched.value = true;
});

watch(summary, (newValue, oldValue) => {
  if (isHydratingForm.value || newValue === oldValue) return;
  summaryTouched.value = true;
});

watch([countries, () => profile.value?.basic], ([countryList, profileBasic]) => {
  if (!countryList?.length || !profileBasic) return;
  applyProfileBasicToForm();
}, { immediate: true });

watch(country, (newValue, oldValue) => {
  if (isHydratingForm.value || newValue === oldValue) return;
  countryTouched.value = true;
  cityTouched.value = true;
  city.value = '';
  setCityValues({
    countryCode: newValue,
    placeName: '',
    postalCode: ''
  });
});

const onAvatarPreviewSaved = ({ imageData, imageType }: { imageData: string; imageType: string }) => {
  setAvatarPreview({
    imageData,
    imageType,
  });
  avatarDialog.value = false;
};

const save = async () => {
  await updateBasicData({
    city: city.value,
    countryIsoCode: country.value,
    profileSummary: summary.value,
  });

  if (avatarPreview.value) {
    const normalizedAvatar = await normalizeAvatarImage(
      avatarPreview.value.imageData,
      avatarPreview.value.imageType,
    );
    await updateAvatar(normalizedAvatar.imageData, normalizedAvatar.imageType);
    if (user.value?.userId) {
      store.commit('avatarCache/resetUserState', 'user');
      store.commit('avatarCache/resetUserState', user.value.userId);
      avatarRerenderKey.value += 1;
    }
    setAvatarPreview(null);
  }
};

return { __sfc: true,user, profile, avatarPreview, setAvatarPreview, route, isBusy, t, isFreelancer, isDirty, countries, store, country, city, summary, avatarDialog, avatarRerenderKey, loadingAnimation, loadingAnimationSize, avatarPreviewSrc, profileInitialValue, hydrationRunId, isHydratingForm, countryTouched, cityTouched, summaryTouched, selectedCity, placeText, fetchCities, inputCity, setCityValues, cities, selectCity, getCountryFromUser, applyProfileBasicToForm, onAvatarPreviewSaved, save, BCXAvatar, ProfileEditActionButtons, ProfileSectionBusyOverlay, BCXSearchSelect, IconAvatarEdit, BCXTextArea, BCXInfoSection, ProfileMaxChars, DataNotice, AvatarUpload }
}

})